import axios from "axios";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { BASE_IMAGE_URL, BASE_URL } from "../../../config/apiConfig";
import { ToolProvider, useToolContext } from "../../../contexts/ToolContext";
import { getHeaders } from "../../../utils/apiUtils";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  MenuItem,
  Select,
  TextField,
  Typography,
  Grid,
} from "@mui/material";
import { useTheme } from "@emotion/react";
import SelectableList from "../../../components/features/GarmentAttribute/SelectableList";
import ImageWindow from "../../../components/layout/Interactive/ImageWindow";
import KeyEventHandler from "../../../contexts/KeyEventHandler";
import MassImageApprovalsFrame from "../../../components/layout/Interactive/MassImageApprovalsFrame";
import { jobQaMap, jobTypes } from "../../../config/jobTypes";
import { showAlert } from "../../../components/common/userFeedback/CustomAlert";
import Slider from "@mui/material/Slider";
import TakeActionScreenCover from "../../../components/common/userFeedback/TakeActionScreenCover";
import LoadingOverlay from "../../../components/common/userFeedback/LoadingOverlay";
import NoDataScreenCover from "../../../components/common/userFeedback/NoDataScreenCover";
import { useAmpltiudeEventTrackingContext } from "../../../contexts/AmplitudeTrackingContext";
import { getCurrentTimestamp } from "../../../utils/dateUtils";
import StyledButton from "../../../components/ui/buttons/StyledButton";
import { formatToSpacedCapitalizedWords } from "../../../utils/sharedFunctions";
import PipelineJobMultiSelect from "../../../components/forms/Dropdowns/PipelineJobMultiSelect";

function ImageAttributeQATool() {
  const {
    jobDetails,
    jobDataLoaded,
    toolType,
    allPipelineJobs,
    shownAttributes,
  } = useToolContext();
  const { palette } = useTheme();
  const [numUsers, setNumUsers] = useState(2);
  const [tagSet, setTagSet] = useState([]);
  const [garments, setGarments] = useState([]);
  const [selectedGarments, setSelectedGarments] = useState([]);
  const [selectAllGarments, setSelectAllGarments] = useState(false);
  const [orderByConfidenceAsc, setOrderByConfidenceAsc] = useState(false);

  const [selectedTag, setSelectedTag] = useState(null);
  const [loading, setLoading] = useState(true);
  const [additionalPossiblePipelineJobs, setAdditionalPossiblePipelineJobs] =
    useState([]);
  const [additionalPipelineJobs, setAdditionalPipelineJobs] = useState([]);
  const { trackEvent } = useAmpltiudeEventTrackingContext();
  const [qaCountObjects, setQaCountObjects] = useState({});
  const [numberOfColumns, setNumberOfColumns] = useState(6); // Default to 3 columns
  const [garmentLimit, setGarmentLimit] = useState(numberOfColumns * 3);

  const [toolID, setToolID] = useState(0);
  const toolName = `ImageTaggingQATool`

  useEffect(() => {
    fetchToolData();
  }, [])

  useEffect(() => {
    setGarmentLimit(numberOfColumns * 3);
  }, [numberOfColumns]);
  
  const fetchToolData = async () => {
    const headers = await getHeaders();
    const formattedToolName = formatToSpacedCapitalizedWords(toolName);
    let url = `${BASE_URL}tools/jobs/tool-names?toolName=${toolName}`;
    axios
      .get(url, headers)
      .then((res) => {
        console.log(`tool info: ${JSON.stringify(res.data.data)}`);
        if (res.data.data && res.data.data.length > 0 && res.data.data[0].id && res.data.data[0].id > 0)
        {
          setToolID(res.data.data[0].id)
        } else {
          const err = `Could not find tool ID in 'tool_names' with name '${formattedToolName}' (formatted from '${toolName}').`
          console.log(err);
          showAlert(err);
        }
      })

      .catch((err) => {
        console.log(err);
        showAlert(err);
      });
  };

  //Axios Calls
  const fetchTagSet = async () => {
    const headers = await getHeaders();
    axios
      .get(
        `${BASE_URL}tools/jobs/tag-set/${jobDetails.pipeline_job_link_id}`,
        headers
      )
      .then((res) => {
        setTagSet(res.data.data);
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
      });
  };
  const fetchPipelineJobIds = async () => {
    const headers = await getHeaders();
    const jobsToFetch = jobQaMap[toolType];
    axios
      .get(
        `${BASE_URL}tools/pipelines/list-pipeline-jobs/${jobsToFetch}`,
        headers
      )
      .then((res) => {
        setAdditionalPossiblePipelineJobs(res.data.data);
        console.log(res.data.data);
      })
      .catch((err) => {
        showAlert(err);
      });
  };

  const fetchNextItemBatch = async () => {
    const headers = await getHeaders();
    const jobsToQa = jobQaMap[toolType];
    const jobIdsToQa = allPipelineJobs
      .filter((job) => jobsToQa.includes(job.job))
      .map((job) => job.pipeline_job_link_id);
    const fullListofIdsToQA = [...additionalPipelineJobs, ...jobIdsToQa];
    const submissionData = {
      graduationConfidence: jobDetails.qa_confidence,
      toolType: toolType,
      pipelineJobIdsToQa: fullListofIdsToQA,
      selectedTag: selectedTag,
      userCount: numUsers,
      jobID: jobDetails.job_id,
      shownAttributes: shownAttributes,
      orderByConfidenceAsc: orderByConfidenceAsc,
      garmentLimit: garmentLimit
    };
    setLoading(true);
    axios
      .post(
        `${BASE_URL}tools/summon/next-item/qa/image-attribute`,
        submissionData,
        headers
      )
      .then((res) => {
        setGarments(res.data.data);
        setLoading(false);
      })
      .catch((err) => {
        setGarments([]);
        setLoading(false);
        showAlert(err);
      });
  };

  const fetchQaCount = async () => {
    const headers = await getHeaders();
    const jobsToQa = jobQaMap[toolType];
    const jobIdsToQa = allPipelineJobs
      .filter((job) => jobsToQa.includes(job.job))
      .map((job) => job.pipeline_job_link_id);
    const fullListofIdsToQA = [...additionalPipelineJobs, ...jobIdsToQa];
    const submissionData = {
      graduationConfidence: jobDetails.qa_confidence,
      toolType: toolType,
      pipelineJobIdsToQa: fullListofIdsToQA,
      userCount: numUsers,
      jobID: jobDetails.job_id,
      shownAttributes: shownAttributes,
      tags: tagSet,
    };
    setLoading(true);
    axios
      .post(`${BASE_URL}tools/jobs/qa/count`, submissionData, headers)
      .then((res) => {
        setQaCountObjects(res.data.data);
        setLoading(false);
      })
      .catch((err) => {
        setQaCountObjects({});
        setLoading(false);
        showAlert(err);
      });
  };

  const submitItems = async () => {
    const headers = await getHeaders();
    setLoading(true);
    const rejectedIds = selectedGarments.map((t) => t.garment_id);
    let approved = garments
      .filter((image) => !rejectedIds.includes(image.garment_id))
      .map((image) => {
        return { garment_id: image.garment_id, image_num: image.image_num };
      });
    let rejected = garments
      .filter((image) => rejectedIds.includes(image.garment_id))
      .map((image) => {
        return { garment_id: image.garment_id, image_num: image.image_num };
      });

    const data = {
      approved: approved,
      rejected: rejected,
      proposal_value_id: selectedTag,
      jobId: jobDetails.job_id,
      pipelineJobId: jobDetails.pipeline_job_link_id,
      toolType: toolType
    };
    await axios
      .post(`${BASE_URL}tools/classify/qa/image-attribute`, data, headers)
      .then((res) => {
        fetchNextItemBatch();
        fetchQaCount();
        setSelectedGarments([]);
        setSelectAllGarments(false);

        trackEvent("ProposalQA", {
          approvedGarmentIds: approved.map(x => x.garment_id),
          rejectedGarmentIds: rejected.map(x => x.garment_id),
          pipelineJobId: jobDetails.pipeline_job_link_id,
          pipelineName: jobDetails.pipeline_name,
          jobId: jobDetails.job_id,
          jobName: jobDetails.job,
          toolType: toolType,
          toolName: "ImageAttributeQATool",
          toolId: toolID,
          confidence: jobDetails.qa_confidence,
          skipped: approved.length === 0,
          qa: true,
        });
      })
      .catch((err) => {
        console.log(err);
        showAlert(err);
      });
  };

  //Use Effects
  useEffect(() => {
    if (jobDataLoaded) {
      fetchTagSet();
      fetchPipelineJobIds();
    }
  }, [jobDataLoaded]);

  useEffect(() => {
    if (selectedTag) {
      fetchNextItemBatch();
    }
  }, [selectedTag, numUsers, additionalPipelineJobs, orderByConfidenceAsc, garmentLimit, numberOfColumns]);

  useEffect(() => {
    if (jobDataLoaded && tagSet && tagSet.length > 0) {
      fetchQaCount();
    }
  }, [tagSet, toolType, numUsers, shownAttributes, additionalPipelineJobs, garmentLimit, numberOfColumns]);

  const getQaCountFromTagId = (tagId) => {
    // qaCountObjects is now an object where keys are tag IDs and values are counts
    return qaCountObjects[tagId] || 0;
  }

  //Handlers
  const handleSliderChange = (event, newValue) => {
    setNumUsers(newValue);
  };

  const handleSelectAllGarments = (selectOrDeselectAll) => {
    const localRejectedTags = garments.map((garm) => {
      return {
        garment_id: garm.garment_id,
        proposed_timestamp: getCurrentTimestamp(),
      };
    });

    setSelectedGarments(selectOrDeselectAll ? localRejectedTags : []);
    setSelectAllGarments(!selectAllGarments);
  };

  return (
    <Box
      sx={{
        p: 2,
        pt: 0,
      }}
    >
      <LoadingOverlay open={loading} />
      <Grid
        container
        style={{
          display: "flex",
          justifyContent: "space-between !important",
          alignItems: "center",
        }}
        spacing={2}
      >
        <Grid item xs={12} sx={{ mt: 2, mb: -1 }}>
          <Typography variant="h2">Image Attribute QA Tool</Typography>
        </Grid>
        <Grid
          item
          xs={12}
          sx={{
            display: "flex",
            flexDirection: "row",
            width: "100%",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              justifyContent: "center",
              flex: 1,
              alignItems: "center",
              pl: 1,
              pr: 1
            }}
          >
            <Select
              value={selectedTag}
              onChange={(e) => setSelectedTag(e.target.value)}
              sx={{
                width: "100%",
              }}
            >
              <MenuItem key={null} value={null}>
                Select Tag
              </MenuItem>
              {tagSet.map((tag, tagIndex) => (
                <MenuItem key={tag.id} value={tag.id}>
                  {tag.name} ({getQaCountFromTagId(tag.id)})
                </MenuItem>
              ))}
            </Select>
          </Box>

          <Box
            sx={{
              justifyContent: "center",
              flex: 1,
              alignItems: "center",
              pl: 1,
              pr: 1,
            }}
          >
            <PipelineJobMultiSelect
              sx={{mt: 0.5, ml: 1}}
              jobNames={['manual_image_tagging']}
              selected={additionalPipelineJobs}
              onSelectedChange={setAdditionalPipelineJobs}
              label={"Select Pipeline Jobs"}
            />
          </Box>

          <Box
            sx={{
              justifyContent: "center",
              flex: 1,
              alignItems: "center",
              pl: 1,
              pr: 1,
              mr: 2
            }}
          >
            <Typography id="input-slider" variant="body" gutterBottom>
              Select a number of users:
            </Typography>
            <Slider
              value={numUsers}
              min={1}
              max={10}
              marks
              step={1}
              onChange={handleSliderChange}
              valueLabelDisplay="auto"
              aria-labelledby="input-slider"
            />
            <Typography variant="body">Selected Number: {numUsers}</Typography>
          </Box>
          
          <Box
            sx={{
              justifyContent: "center",
              flex: 1,
              alignItems: "center",
              pl: 1,
              pr: 1,
              mr: 2
            }}
          >
            <Select
              value={numberOfColumns}
              onChange={(e) => setNumberOfColumns(e.target.value)}
              displayEmpty
            >
              <MenuItem value={4}>4 Columns</MenuItem>
              <MenuItem value={5}>5 Columns</MenuItem>
              <MenuItem value={6}>6 Columns</MenuItem>
              <MenuItem value={7}>7 Columns</MenuItem>
              <MenuItem value={8}>8 Columns</MenuItem>
            </Select>
          </Box>

          <StyledButton
            variant="contained"
            color="primary"
            disabled={loading}
            onClick={() => handleSelectAllGarments(!selectAllGarments)}
            sx={{...{mr: 2, mt: -2},
            ...selectAllGarments && {
              backgroundColor: `${palette.primary[300]} !important`,
              color: `${palette.primary.main} !important`
            }}}
          >
            {selectAllGarments ? `DESELECT` : `SELECT`} ALL
          </StyledButton>

          <StyledButton
            variant="contained"
            color="primary"
            disabled={loading}
            sx={[{
              //overflow: "hidden",
              //whiteSpace: "nowrap"
              mr: 2, mt: -2
              },
              orderByConfidenceAsc && {
                  backgroundColor: `${palette.primary[300]} !important`,
                  color: `${palette.primary.main} !important`
              }]}
            onClick={() => setOrderByConfidenceAsc(!orderByConfidenceAsc)}
          >
            CONFIDENCE {orderByConfidenceAsc ? `LOW -> HIGH` : `HIGH -> LOW`}
          </StyledButton>

          <StyledButton
            variant="outlined"
            color="primary"
            disabled={loading}
            onClick={() => submitItems()}
            sx={{ mt: -2 }}
          >
            SUBMIT
          </StyledButton>
        </Grid>
      </Grid>
      {selectedTag ? (
        (garments.length === 0) & !loading ? (
          <NoDataScreenCover showBackButton={false} sx={{mt: 2}}/>
        ) : (
          <MassImageApprovalsFrame
            tags={tagSet}
            selectedTag={selectedTag}
            setSelectedTag={setSelectedTag}
            garments={garments}
            numberOfColumns={numberOfColumns}
            selectedGarments={selectedGarments}
            setSelectedGarments={setSelectedGarments}
            shownAttributes={shownAttributes}
            toolType={toolType}
            groupRepresentativeBorderModal={true}
          />
        )
      ) : (
        <TakeActionScreenCover />
      )}
    </Box>
  );
}

function WrappedImageAttributeQATool() {
  //Job Pipeline ID
  const { id } = useParams();
  return (
    <ToolProvider id={id}>
      <ImageAttributeQATool />
    </ToolProvider>
  );
}

export default WrappedImageAttributeQATool;
